import { PERMIT_ACTIONS } from './permit-actions';
import { ResourceEditorContext } from '../shared/models/resource-editor-context';
import { createFilterParams } from '../shared/functions/create-filter-params';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
} from '../shared/models/main-table-filter.model';
import { MainTableFilterModulesEnum } from '../core/enums/main-table-filter-modules.enum';
import { MainTableFilteringHelperService } from '../shared/main-table-module/services/main-table-filtering-helper.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { getDepartmentId, getTenantId } from '../services/store-snapshot.service';
import { ApiResponse } from '../core/models/api-response';
import { FormDataService } from '../shared/attachments/service/form-data.service';
import { IAttachment } from '../shared/attachments/models/attachments.model';
import { IGenericLogFieldChange } from '../shared/models/generic-log.model';

export type PermitLifecycleInput =
  | { workCompletedAndAreaNormalized: boolean; endTime: string }
  | { startTime: string | null };

export enum PermitState {
  Draft = 1,
  ForApproval = 2,
  Pending = 3,
  InProgress = 4,
  Finished = 5,
  Closed = 6,
  Rejected = 7,
}

export enum PermitUrgency {
  Normal = 0,
  DueSoon = 1,
  Overdue = 2,
}

export interface PermitOption {
  id: string;
  name: string;
}
export interface PermitPersonOption extends PermitOption {
  userId: string;
  positionName: string | null;
  isOnBoard: boolean;
}
export interface PermitRiskAssessmentOption extends PermitOption {
  riskAssessment: PermitOption;
  performedAt: string;
}
interface PermitFormResult {
  form: PermitOption;
  formVersionId: string;
  customFormResponse: string | null;
  canRemove?: boolean;
}

export interface PermitAssignedForm extends PermitOption {
  versionId?: string;
  responseId: string | null;
  canRemove?: boolean;
}
export interface PermitEditorOptions {
  employmentVersions: PermitPersonOption[];
  plannedActivityVersions: PermitPersonOption[];
  riskAssessmentVersions: PermitRiskAssessmentOption[];
  components: PermitOption[];
  locationTypes: PermitOption[];
  types?: PermitTypeSettings[];
}

export interface PermitListFilters {
  state?: PermitState | null;
  typeId?: string | null;
  location?: string;
  prolonged?: boolean;
  periodFrom?: string | null;
  periodTo?: string | null;
}
export interface PermitTypeSummary extends PermitOption {
  versionId: string;
  isActive: boolean;
}
export interface PermitTypeSettings extends PermitTypeSummary {
  positionsResponsibleForApproval: string[];
  positionGroupsResponsibleForApproval: string[];
  rolesResponsibleForApproval: string[];
  formIds: string[];
  preApprovalFormIds?: string[];
  duringWorkFormIds?: string[];
  attachments?: IAttachment[];
  formOptions?: PermitOption[];
  preApprovalFormOptions?: PermitOption[];
  links: string[];
}
export type PermitLocationSettings = PermitOption;
export interface ToolboxTalkSettings {
  templateId: string | null;
  templateVersionId: string | null;
  formId: string | null;
  formName: string | null;
}
export interface PermitTypeSave {
  name: string;
  positionsResponsibleForApproval: string[];
  positionGroupsResponsibleForApproval: string[];
  rolesResponsibleForApproval: string[];
  formIds: string[];
  preApprovalFormIds?: string[];
  links: string[];
  attachments?: IAttachment[];
}

interface PermitTypeApiResult {
  id: string;
  name: string;
  typeVersionId: string;
  isActive: boolean;
  positionsResponsibleForApproval: PermitOption[];
  positionGroupsResponsibleForApproval: PermitOption[];
  rolesResponsibleForApproval: PermitOption[];
  defaultForms: PermitOption[];
  defaultPreApprovalForms?: PermitOption[];
  defaultAttachments?: IAttachment[];
  links: string[];
}

interface ToolboxTalkSettingsApiResult {
  templateId: string | null;
  templateVersionId: string | null;
  form: PermitOption | null;
}
export interface PermitLogRecord {
  versionId: string;
  action: string;
  createdBy: string;
  date: string;
  comment: string | null;
  fieldsChanged: IGenericLogFieldChange[];
}
export interface PermitDashboard {
  myForApproval: number;
  allForApproval: number;
  myRejected: number;
  myApproved: number;
  allApproved: number;
  allFinished: number;
  myInProgressOnTime: number;
  allInProgressOnTime: number;
  myInProgressDueSoon: number;
  allInProgressDueSoon: number;
  myInProgressOverdue: number;
  allInProgressOverdue: number;
  draft: number;
  forApproval: number;
  pending: number;
  inProgress: number;
  finished: number;
  closed: number;
  dueSoon: number;
  overdue: number;
  active: PermitDashboardItem[];
}

export interface PermitDashboardItem {
  permitId: string;
  readableId: string;
  name: string;
  state: PermitState;
  start: string;
  end: string;
  urgency: PermitUrgency;
}

export interface PermitWorkNotice {
  permitId: string;
  readableId: string;
  name: string;
  state: PermitState;
  type: PermitOption;
  locationType: PermitOption;
  specificLocation: string | null;
  start: string;
  end: string;
}

export type PermitRelatedObjects = Pick<
  import('../shared/interconnected-objects/models/interconnected-object.model').InterconnectedObjectRequest,
  | 'documents'
  | 'riskAssessments'
  | 'works'
  | 'maintenanceJobs'
  | 'certificates'
  | 'nonConformances'
  | 'drills'
  | 'components'
  | 'serviceReports'
>;

export interface PermitContent {
  duringWorkFormIds?: string[];
  relatedObjects?: PermitRelatedObjects;
  preApprovalFormIds?: string[];
  preApprovalFormResponses?: { formVersionId: string; response: string }[];
  name: string;
  description: string | null;
  typeId: string | null;
  locationTypeId: string | null;
  specificLocation: string | null;
  equipmentWorkedOn: string[];
  equipmentAffected: string[];
  componentWorkedOnIds: string[];
  componentAffectedIds: string[];
  schedule: { startImmediately: boolean; start: string | null; end: string | null } | null;
  supervisorUserId: string | null;
  externalSupervisor: string | null;
  externalSupervisorCompany: string | null;
  performersOnlyOnBoard: boolean;
  stakeholdersOnlyOnBoard: boolean;
  performerEmploymentVersionIds: string[];
  performerPlannedActivityVersionIds: string[];
  otherPerformers: string[];
  stakeholderUserIds: string[];
  otherStakeholders: string[];
  usersResponsibleForApproval: string[];
  positionsResponsibleForApproval: string[];
  positionGroupsResponsibleForApproval: string[];
  rolesResponsibleForApproval: string[];
  links: string[];
  attachments: IAttachment[];
  riskEvidence: {
    performanceVersionId: string | null;
    attachmentDetails: IAttachment[];
    newAttachments?: File[];
  } | null;
}

export type PermitSelectedPerson = Omit<PermitPersonOption, 'isOnBoard'>;
export interface PermitInProgressChanges extends Partial<PermitContent> {
  formResponses?: { formVersionId: string; response: string }[];
}

export interface PermitPerformers {
  onlyOnBoard: boolean;
  employmentVersions: PermitSelectedPerson[];
  plannedActivityVersions: PermitSelectedPerson[];
  others: string[];
}
export interface PermitStakeholders {
  onlyOnBoard: boolean;
  users: PermitOption[];
  others: string[];
}
export interface PermitApprovers {
  users: PermitOption[];
  positions: PermitOption[];
  positionGroups: PermitOption[];
  roles: PermitOption[];
}

interface PermitContentResult extends Omit<
  PermitContent,
  | 'performersOnlyOnBoard'
  | 'stakeholdersOnlyOnBoard'
  | 'otherPerformers'
  | 'otherStakeholders'
  | 'typeId'
  | 'locationTypeId'
  | 'supervisorUserId'
  | 'performerEmploymentVersionIds'
  | 'performerPlannedActivityVersionIds'
  | 'stakeholderUserIds'
  | 'usersResponsibleForApproval'
  | 'positionsResponsibleForApproval'
  | 'positionGroupsResponsibleForApproval'
  | 'rolesResponsibleForApproval'
  | 'componentWorkedOnIds'
  | 'componentAffectedIds'
  | 'riskEvidence'
  | 'relatedObjects'
> {
  type: PermitOption | null;
  locationType: PermitOption | null;
  supervisor: PermitOption | null;
  performers: PermitPerformers;
  stakeholders: PermitStakeholders;
  approvers: PermitApprovers;
  componentsWorkedOn: PermitOption[];
  componentsAffected: PermitOption[];
  relatedObjects: { [Key in keyof PermitRelatedObjects]: PermitOption[] };
  riskEvidence: {
    performanceVersion: PermitOption | null;
    attachmentDetails: IAttachment[];
  } | null;
}

export interface PermitProlongedVersion {
  id: string;
  schedule: PermitContent['schedule'];
}

export interface GetPermitToWorkQueryResult extends PermitContentResult {
  actions?: string[];
  actionFields?: ResourceEditorContext['actionFields'];
  isReadOnly?: boolean;
  prolongedVersionIds?: string[];
  prolongedVersions?: PermitProlongedVersion[];
  id: string;
  readableId: string;
  name: string;
  state: PermitState;
  currentVersionId: string;
  deleted: boolean;
  isProlonged: boolean;
  forms: PermitFormResult[];
  preApprovalForms: PermitFormResult[];
  toolboxTalk: PermitToWorkView['toolboxTalk'];
}

export interface PermitToWorkListItemView {
  id: string;
  readableId: string;
  name: string;
  initiatorName: string | null;
  approverUserNames: string[];
  approverPositionNames: string[];
  approverPositionGroupNames: string[];
  approverRoleNames: string[];
  state: PermitState;
  currentVersionId: string;
  deleted: boolean;
  isProlonged: boolean;
  start: string | null;
  end: string | null;
  typeName: string | null;
  location: string | null;
  urgency: PermitUrgency | null;
}

export interface PermitToWorkView extends PermitContent {
  riskAssessmentVersion?: PermitOption | null;
  actions?: string[];
  actionFields?: ResourceEditorContext['actionFields'];
  id: string;
  readableId: string;
  name: string;
  state: PermitState;
  currentVersionId: string;
  start?: string | null;
  end?: string | null;
  typeName?: string | null;
  initiatorName?: string | null;
  performers: PermitPerformers;
  stakeholders: PermitStakeholders;
  approvers: PermitApprovers;
  workedOnComponents?: PermitOption[];
  affectedComponents?: PermitOption[];
  referenceOptions?: PermitOption[];
  location?: string | null;
  urgency?: PermitUrgency | null;
  deleted: boolean;
  isReadOnly: boolean;
  isProlonged: boolean;
  prolongedVersions: PermitProlongedVersion[];
  toolboxTalk: {
    id: string | null;
    form: PermitOption;
    formVersionId: string;
    templateVersionId: string;
    customFormResponse: string | null;
    performedAt: string | null;
    performedBy: string | null;
    participants: string[];
    otherParticipants: string[];
    links: string[];
    attachments: IAttachment[];
    description: string | null;
    comment: string | null;
  } | null;
  forms: PermitAssignedForm[];
  preApprovalForms?: PermitAssignedForm[];
}

export const PTW_PRIVILEGES = {
  read: 'PTW_READ',
  create: 'PTW_CREATE',
  update: 'PTW_UPDATE',
  settings: 'PTW_SETTINGS',
  delete: 'PTW_DELETE',
} as const;

@Injectable({ providedIn: 'root' })
export class PermitToWorkApiService {
  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/PermitToWork`;
  }

  createContext(): Observable<ResourceEditorContext> {
    return this.http.get<ResourceEditorContext>(`${this.url}/CreateContext`);
  }

  prolongContext(id: string): Observable<ResourceEditorContext> {
    return this.http.get<ResourceEditorContext>(`${this.url}/${id}/ProlongContext`);
  }

  list(
    page = 0,
    search = '',
    tab = 'Open',
    filters: PermitListFilters = {}
  ): Observable<ApiResponse<PermitToWorkListItemView[]>> {
    let params = new HttpParams()
      .set('Page', page)
      .set('PageSize', 25)
      .set('GlobalSearch', search)
      .set('Tab', tab);
    if (filters.state != null) params = params.append('States', filters.state);
    if (filters.typeId) params = params.set('TypeId', filters.typeId);
    if (filters.location?.trim()) params = params.set('Location', filters.location.trim());
    if (filters.prolonged) params = params.set('Prolonged', true);
    if (filters.periodFrom) params = params.set('PeriodFrom', filters.periodFrom);
    if (filters.periodTo) params = params.set('PeriodTo', filters.periodTo);
    return this.http.get<ApiResponse<PermitToWorkListItemView[]>>(this.url, { params });
  }

  getTable(filter: MainTableFilterModel): Observable<ApiResponse<any[]>> {
    const root = `${environment.apiUrl}/api/${getTenantId()}/PermitToWork`;
    const module = filter.module;
    const url =
      module === MainTableFilterModulesEnum.PermitToWorkTypes
        ? `${root}/Types`
        : module === MainTableFilterModulesEnum.PermitToWorkLocations
          ? `${root}/LocationTypes`
          : module === MainTableFilterModulesEnum.PermitToWorkLogs
            ? `${this.url}/${filter.additional.permitId}/log`
            : this.url;
    let params = createFilterParams(filter);
    if (module === MainTableFilterModulesEnum.PermitToWork) {
      params = params.set('Tab', filter.additional?.tab ?? 'Open');
      if (filter.additional?.prolonged) params = params.set('Prolonged', true);
    } else if (module === MainTableFilterModulesEnum.PermitToWorkTypes)
      params = params.set('IncludeInactive', true);
    params = MainTableFilteringHelperService.appendFilteringParams(params, filter);
    return this.http.get<ApiResponse<any[]>>(url, { params }).pipe(
      map((response) => ({
        ...response,
        data:
          module === MainTableFilterModulesEnum.PermitToWorkTypes
            ? response.data.map((item) => ({ ...item, versionId: item.typeVersionId }))
            : module === MainTableFilterModulesEnum.PermitToWorkLogs
              ? response.data.map((item) => ({
                  ...item,
                  action: `ptw.action.${item.action}`,
                  fieldsChanged: item.fieldsChanged.map(
                    (change: PermitLogRecord['fieldsChanged'][number]) =>
                      this.logFieldChange(change)
                  ),
                }))
              : response.data,
      }))
    );
  }

  private logFieldChange(
    change: PermitLogRecord['fieldsChanged'][number]
  ): PermitLogRecord['fieldsChanged'][number] {
    if (change.fieldName === 'preApprovalFormResponses' || change.fieldName === 'formResponses')
      return {
        ...change,
        labelKey:
          change.fieldName === 'preApprovalFormResponses'
            ? 'ptw.preApprovalForms'
            : 'ptw.duringWorkForms',
      };
    if (change.fieldName === 'state')
      return {
        ...change,
        fieldName: 'status',
        value: change.value ? this.translate.instant(`ptw.state.${change.value}`) : change.value,
        oldValue: change.oldValue
          ? this.translate.instant(`ptw.state.${change.oldValue}`)
          : change.oldValue,
      };
    if (
      change.fieldName.startsWith('relatedObjects.') ||
      change.fieldName.startsWith('toolboxTalk.relatedObjects.')
    )
      return {
        ...change,
        labelKey: change.fieldName
          .split('.')
          .filter((name) => name !== 'relatedObjects')
          .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
          .join(''),
      };
    return {
      ...change,
      fieldName: change.fieldName
        .split('.')
        .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
        .join(''),
    };
  }

  get(id: string, prolongedVersionId?: string | null): Observable<PermitToWorkView> {
    const suffix = prolongedVersionId ? `/Prolongs/${prolongedVersionId}` : '';
    return this.http
      .get<GetPermitToWorkQueryResult>(`${this.url}/${id}${suffix}`)
      .pipe(map((permit) => this.mapPermit(permit, !!prolongedVersionId)));
  }

  log(id: string, page = 0): Observable<ApiResponse<PermitLogRecord[]>> {
    return this.http.get<ApiResponse<PermitLogRecord[]>>(`${this.url}/${id}/log`, {
      params: { Page: page, PageSize: 25 },
    });
  }

  options(kind: 'Types' | 'LocationTypes'): Observable<PermitOption[]> {
    return this.http.get<PermitOption[]>(
      `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/${kind}/List`
    );
  }

  editorOptions(): Observable<PermitEditorOptions> {
    return this.http
      .get<
        Omit<PermitEditorOptions, 'types'> & { types?: PermitTypeApiResult[] }
      >(`${this.url}/Options`)
      .pipe(
        map((options) => ({
          ...options,
          types: (options.types ?? []).map((type) => this.mapType(type)),
        }))
      );
  }

  dashboard(
    departmentId = getDepartmentId(),
    tenantId = getTenantId()
  ): Observable<PermitDashboard> {
    const url = `${environment.apiUrl}/api/${tenantId}/PermitToWork/Dashboard`;
    return this.http.get<PermitDashboard>(url, { params: { DepartmentId: departmentId } });
  }

  workNotices(departmentId: string, tenantId: string): Observable<PermitWorkNotice[]> {
    return this.http.get<PermitWorkNotice[]>(
      `${environment.apiUrl}/api/${tenantId}/${departmentId}/PermitToWork/NotificationInformation`
    );
  }

  listTypes(includeInactive = true): Observable<ApiResponse<PermitTypeSummary[]>> {
    return this.http
      .get<
        ApiResponse<PermitTypeApiResult[]>
      >(`${environment.apiUrl}/api/${getTenantId()}/PermitToWork/Types`, { params: { Page: 0, PageSize: 1000, IncludeInactive: includeInactive } })
      .pipe(
        map((response) => ({
          ...response,
          data: response.data.map((item) => ({
            id: item.id,
            name: item.name,
            versionId: item.typeVersionId,
            isActive: item.isActive,
          })),
        }))
      );
  }

  getType(id: string): Observable<PermitTypeSettings> {
    return this.http
      .get<PermitTypeApiResult>(
        `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/Types/${id}`
      )
      .pipe(map((item) => this.mapType(item)));
  }

  saveType(value: PermitTypeSave, id?: string): Observable<{ id: string }> {
    const url = `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/Types${id ? `/${id}` : ''}`;
    const request = {
      name: value.name,
      positionsResponsibleForApproval: value.positionsResponsibleForApproval,
      positionGroupsResponsibleForApproval: value.positionGroupsResponsibleForApproval,
      rolesResponsibleForApproval: value.rolesResponsibleForApproval,
      defaultFormIds: value.formIds,
      defaultPreApprovalFormIds: value.preApprovalFormIds ?? [],
      links: value.links,
      defaultAttachments: value.attachments ?? [],
    };
    const body = FormDataService.mapToFormData(request);
    return id
      ? this.http.put<{ id: string }>(url, body)
      : this.http.post<{ id: string }>(url, body);
  }

  setTypeActive(id: string, isActive: boolean): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/Types/${id}/Active`,
      { isActive }
    );
  }

  listLocations(): Observable<ApiResponse<PermitLocationSettings[]>> {
    return this.http.get<ApiResponse<PermitLocationSettings[]>>(
      `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/LocationTypes`,
      { params: { Page: 0, PageSize: 1000 } }
    );
  }

  getLocation(id: string): Observable<PermitLocationSettings | undefined> {
    const filter = new MainTableFilterModel(MainTableFilterModulesEnum.PermitToWorkLocations);
    filter.pageSize = 1;
    filter.searchBy = [new MainTableFilterSearchByModel('id', id, 'Equal')];
    return this.getTable(filter).pipe(map((response) => response.data[0]));
  }

  createLocation(name: string): Observable<PermitOption> {
    return this.http.post<PermitOption>(
      `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/LocationTypes`,
      { name }
    );
  }

  updateLocation(id: string, name: string): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/LocationTypes/${id}`,
      { name }
    );
  }

  getToolboxTalkSettings(): Observable<ToolboxTalkSettings> {
    return this.http
      .get<ToolboxTalkSettingsApiResult>(
        `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/TbtSettings`
      )
      .pipe(map((settings) => this.mapToolboxTalkSettings(settings)));
  }

  updateToolboxTalkSettings(formId: string): Observable<ToolboxTalkSettings> {
    return this.http
      .put<ToolboxTalkSettingsApiResult>(
        `${environment.apiUrl}/api/${getTenantId()}/PermitToWork/TbtSettings`,
        { formId }
      )
      .pipe(map((settings) => this.mapToolboxTalkSettings(settings)));
  }

  saveForAction(
    id: string | undefined,
    action: string,
    changes: Partial<PermitContent>,
    comment?: string
  ): Observable<void | { id: string }> {
    if (!id) {
      if (action === PERMIT_ACTIONS.CreateDraft) return this.create(changes, 'Draft');
      if (action === PERMIT_ACTIONS.CreateAndSubmit) return this.create(changes, 'ForApproval');
    } else {
      switch (action) {
        case PERMIT_ACTIONS.SaveDraft:
          return this.saveDraft(id, changes);
        case PERMIT_ACTIONS.SaveAndSubmit:
          return this.saveDraft(id, changes, 'ForApproval');
        case PERMIT_ACTIONS.Review:
          return this.review(id, changes);
        case PERMIT_ACTIONS.SaveAndApprove:
          return this.review(id, changes, true, comment);
        case PERMIT_ACTIONS.EditApproved:
          return this.saveApproved(id, changes);
        case PERMIT_ACTIONS.EditInProgress:
          return this.saveInProgress(id, changes);
        case PERMIT_ACTIONS.EditFinished:
          return this.saveFinished(id, changes);
      }
    }
    return throwError(() => new Error('Unsupported permit save action.'));
  }

  create(
    changes: Partial<PermitContent>,
    saveType: 'Draft' | 'ForApproval'
  ): Observable<{ id: string }> {
    const url = saveType === 'ForApproval' ? `${this.url}/SaveAndForApproval` : this.url;
    return this.http.post<{ id: string }>(url, this.contentFormData(changes));
  }

  saveDraft(
    id: string,
    changes: Partial<PermitContent>,
    saveType: 'Draft' | 'ForApproval' = 'Draft'
  ): Observable<void> {
    return this.http.put<void>(
      `${this.url}/${id}/${saveType === 'ForApproval' ? 'SaveAndForApproval' : 'Draft'}`,
      this.contentFormData(changes)
    );
  }

  review(
    id: string,
    changes: Partial<PermitContent>,
    approve = false,
    comment?: string
  ): Observable<void> {
    return this.updateContent(id, approve ? 'SaveAndApprove' : 'Review', {
      ...changes,
      ...(approve ? { comment } : {}),
    });
  }

  saveApproved(id: string, changes: Partial<PermitContent>): Observable<void> {
    return this.updateContent(id, 'Approved', changes);
  }

  saveInProgress(id: string, changes: PermitInProgressChanges): Observable<void> {
    return this.updateContent(id, 'InProgress', changes);
  }

  saveFinished(id: string, changes: Partial<PermitContent>): Observable<void> {
    return this.updateContent(id, 'Finished', changes);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${id}`);
  }

  transition(
    id: string,
    action: 'Submit' | 'Approve' | 'Reject' | 'SetInProgress' | 'Finish' | 'Close',
    comment?: string,
    details?: PermitLifecycleInput
  ): Observable<void> {
    const body =
      action === 'Reject' || action === 'Approve'
        ? { comment }
        : action === 'Finish'
          ? { ...details, comment }
          : action === 'Submit'
            ? null
            : {};
    let params = new HttpParams();
    if (action === 'SetInProgress' && details && 'startTime' in details && details.startTime)
      params = params.set('StartTime', details.startTime);
    return this.http.post<void>(`${this.url}/${id}/${action}`, body, { params });
  }

  copyToDraft(id: string): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${this.url}/${id}/CopyToDraft`, {});
  }

  prolong(
    id: string,
    changes: Partial<PermitContent>,
    approveImmediately = false
  ): Observable<void> {
    return this.http.post<void>(
      `${this.url}/${id}/Prolong`,
      this.contentFormData({ ...changes, approveImmediately })
    );
  }

  performToolboxTalk(
    id: string,
    request: {
      templateVersionId: string;
      formVersionId: string;
      response: string;
      participantEmploymentVersionIds: string[];
      participantPlannedActivityVersionIds: string[];
      otherParticipants: string[];
      links: string[];
      attachments: IAttachment[];
      description?: string;
      comment?: string;
      relatedObjects?: PermitRelatedObjects;
    }
  ): Observable<void> {
    const body = FormDataService.mapToFormData(request);
    return this.http.post<void>(`${this.url}/${id}/Tbt/Perform`, body);
  }

  formDefinitionUrl(formId: string, formVersionId?: string): string {
    const version = formVersionId ? `?VersionId=${formVersionId}` : '';
    return `${environment.apiUrl}/api/${getTenantId()}/CustomForm/definition/${formId}${version}`;
  }

  private updateContent(
    id: string,
    path: 'Review' | 'SaveAndApprove' | 'Approved' | 'InProgress' | 'Finished',
    changes: Partial<PermitContent> & { comment?: string }
  ): Observable<void> {
    const { comment, ...content } = changes;
    return this.http.put<void>(
      `${this.url}/${id}/${path}`,
      this.contentFormData({ changes: content, ...(comment !== undefined ? { comment } : {}) })
    );
  }

  private contentFormData(content: object): FormData {
    const body = FormDataService.mapToFormData(content);
    this.appendExplicitClears(body, content);
    return body;
  }

  private appendExplicitClears(body: FormData, value: object, prefix = ''): void {
    Object.entries(value).forEach(([key, item]) => {
      const field = prefix ? `${prefix}.${key}` : key;
      if (item === null || (Array.isArray(item) && item.length === 0)) {
        body.append(field, '');
      } else if (item === false) {
        body.set(field, 'false');
      } else if (
        item &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        !(item instanceof File)
      ) {
        this.appendExplicitClears(body, item, field);
      }
    });
  }

  private mapType(item: PermitTypeApiResult): PermitTypeSettings {
    return {
      id: item.id,
      name: item.name,
      versionId: item.typeVersionId,
      isActive: item.isActive,
      positionsResponsibleForApproval: item.positionsResponsibleForApproval.map((x) => x.id),
      positionGroupsResponsibleForApproval: item.positionGroupsResponsibleForApproval.map(
        (x) => x.id
      ),
      rolesResponsibleForApproval: item.rolesResponsibleForApproval.map((x) => x.id),
      formIds: item.defaultForms.map((x) => x.id),
      preApprovalFormIds: (item.defaultPreApprovalForms ?? []).map((x) => x.id),
      formOptions: item.defaultForms,
      preApprovalFormOptions: item.defaultPreApprovalForms ?? [],
      attachments: (item.defaultAttachments ?? []).map((attachment) => ({
        ...this.mapPermitAttachment(attachment, getTenantId()),
        isNew: false,
      })),
      links: item.links,
    };
  }

  private mapToolboxTalkSettings(settings: ToolboxTalkSettingsApiResult): ToolboxTalkSettings {
    return {
      templateId: settings.templateId,
      templateVersionId: settings.templateVersionId,
      formId: settings.form?.id ?? null,
      formName: settings.form?.name ?? null,
    };
  }

  private mapPermit(permit: GetPermitToWorkQueryResult, historical = false): PermitToWorkView {
    const {
      type,
      locationType,
      supervisor,
      performers,
      stakeholders,
      approvers,
      componentsWorkedOn,
      componentsAffected,
      relatedObjects,
      riskEvidence,
      ...metadata
    } = permit;
    const now = Date.now();
    const end = permit.schedule?.end ? new Date(permit.schedule.end).getTime() : null;
    const active = permit.state === PermitState.Pending || permit.state === PermitState.InProgress;
    return {
      ...metadata,
      performers,
      stakeholders,
      approvers,
      actions: historical ? [] : permit.actions,
      actionFields: historical ? {} : permit.actionFields,
      forms: permit.forms.map((form) => this.mapAssignedForm(form)),
      preApprovalForms: permit.preApprovalForms.map((form) => this.mapAssignedForm(form)),
      typeName: type?.name ?? null,
      riskAssessmentVersion: riskEvidence?.performanceVersion ?? null,
      location: [locationType?.name, permit.specificLocation].filter(Boolean).join(' · '),
      urgency:
        active && end !== null
          ? end <= now
            ? PermitUrgency.Overdue
            : end < now + 3600000
              ? PermitUrgency.DueSoon
              : PermitUrgency.Normal
          : null,
      isReadOnly:
        historical || !!permit.isReadOnly || permit.deleted || permit.state === PermitState.Closed,
      prolongedVersions:
        permit.prolongedVersions ??
        (permit.prolongedVersionIds ?? []).map((id) => ({ id, schedule: null })),
      workedOnComponents: componentsWorkedOn,
      affectedComponents: componentsAffected,
      referenceOptions: Object.values(relatedObjects).flat(),
      toolboxTalk: permit.toolboxTalk
        ? {
            ...permit.toolboxTalk,
            attachments: permit.toolboxTalk.attachments.map((attachment) =>
              this.mapPermitAttachment(attachment, getTenantId())
            ),
          }
        : null,
      attachments: permit.attachments.map((attachment) =>
        this.mapPermitAttachment(attachment, getTenantId())
      ),
      typeId: permit.type?.id ?? null,
      locationTypeId: permit.locationType?.id ?? null,
      supervisorUserId: permit.supervisor?.id ?? null,
      performersOnlyOnBoard: performers.onlyOnBoard,
      stakeholdersOnlyOnBoard: stakeholders.onlyOnBoard,
      performerEmploymentVersionIds: performers.employmentVersions.map((person) => person.id),
      performerPlannedActivityVersionIds: performers.plannedActivityVersions.map(
        (person) => person.id
      ),
      otherPerformers: performers.others,
      stakeholderUserIds: stakeholders.users.map((person) => person.id),
      otherStakeholders: stakeholders.others,
      usersResponsibleForApproval: approvers.users.map((person) => person.id),
      positionsResponsibleForApproval: approvers.positions.map((position) => position.id),
      positionGroupsResponsibleForApproval: approvers.positionGroups.map((group) => group.id),
      rolesResponsibleForApproval: approvers.roles.map((role) => role.id),
      componentWorkedOnIds: permit.componentsWorkedOn.map((component) => component.id),
      componentAffectedIds: permit.componentsAffected.map((component) => component.id),
      relatedObjects: {
        documents: permit.relatedObjects.documents.map((reference) => reference.id),
        riskAssessments: permit.relatedObjects.riskAssessments.map((reference) => reference.id),
        works: permit.relatedObjects.works.map((reference) => reference.id),
        maintenanceJobs: permit.relatedObjects.maintenanceJobs.map((reference) => reference.id),
        certificates: permit.relatedObjects.certificates.map((reference) => reference.id),
        nonConformances: permit.relatedObjects.nonConformances.map((reference) => reference.id),
        drills: permit.relatedObjects.drills.map((reference) => reference.id),
        components: permit.relatedObjects.components.map((reference) => reference.id),
        serviceReports: permit.relatedObjects.serviceReports.map((reference) => reference.id),
      },
      riskEvidence: permit.riskEvidence
        ? {
            performanceVersionId: permit.riskEvidence.performanceVersion?.id ?? null,
            attachmentDetails: permit.riskEvidence.attachmentDetails.map((attachment) =>
              this.mapPermitAttachment(attachment, getTenantId())
            ),
          }
        : null,
    };
  }

  private mapAssignedForm(entry: PermitFormResult): PermitAssignedForm {
    return {
      ...entry.form,
      versionId: entry.formVersionId,
      responseId: entry.customFormResponse,
      canRemove: entry.canRemove,
    };
  }

  private mapPermitAttachment(attachment: IAttachment, scopeId = getTenantId()): IAttachment {
    return {
      ...attachment,
      link: attachment.link || `${environment.apiUrl}/api/${scopeId}/DownloadFile/${attachment.id}`,
      type: attachment.type || attachment.name.split('.').pop()?.toLowerCase() || 'other',
      categories: attachment.categories ?? [],
    };
  }
}
